import { styled } from '@aether/styles'
import { Button, Media } from '@aether/components'
import { InsiderPage } from '@aether/models'
import { InView } from 'react-intersection-observer'
import { useState } from 'react'
import { useCartContext } from '@aether/cart'
import { useTranslation } from 'react-i18next'
import { useCustomerContext } from '@aether/account/utils-customer-context'

interface InsiderHeroProps {
  hero: InsiderPage['hero']
  sellingPlanId?: string
  isFooterVisible?: boolean
}

const StyledHero = styled('section', {
  width: '100%',
  maxWidth: '100vw',
  height: '550px',
  display: 'grid',
  gridTemplateRows: '1fr auto',
  color: '$white',
  padding: '$32 $24',
  position: 'relative',
  zIndex: 1,

  '@md': {
    height: '780px',
    padding: '$48 $64',
  },
})

const BackgroundWrapper = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  zIndex: -1,
})

const Content = styled('div', {
  display: 'grid',
  gridTemplateRows: '1fr auto',
})

const BottomContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
  justifyContent: 'flex-end',
  height: '100%',

  '@md': {
    gap: '$28',
  },
})

const Title = styled('h1', {
  $aetherFont: 'heading03',
  borderTop: '1px solid $white',
  paddingTop: '$16',
})

const Description = styled('p', {
  $aetherFont: 'heading05',
  maxWidth: '300px',

  '@md': {
    maxWidth: '390px',
  },
})

const AddToCart = styled(Button, {
  width: '100%',

  '@md': {
    maxWidth: '300px',
  },
})

const StickyButtonContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  transform: 'translateY(100%)',
  transition: 'transform 0.3s ease-out',
  zIndex: `$content`,
  padding: '$24',

  variants: {
    isVisible: {
      true: {
        transform: 'translateY(0)',
      },
    },
  },

  '@md': {
    padding: '$40',
  },
})

const StickyButton = styled(Button, {
  margin: '0 auto',
  width: '100%',
  maxWidth: '300px',
})

export const InsiderHero = ({
  hero,
  sellingPlanId,
  isFooterVisible = false,
}: InsiderHeroProps) => {
  const { t } = useTranslation('product')
  const [showStickyButton, setShowStickyButton] = useState(false)
  const { addCartLines, setCartModalOpen } = useCartContext()
  const { userRole } = useCustomerContext()
  const isVip = userRole === 'vip'

  const handleAddToCart = async () => {
    const membershipMerchandiseId = hero?.productLink?.variantId

    if (!membershipMerchandiseId) {
      return console.error('Membership merchandise ID missing')
    }
    if (!sellingPlanId) {
      return console.error('Membership selling plan ID missing')
    }

    const id = membershipMerchandiseId.replace(
      'shopifyProductVariant-',
      'gid://shopify/ProductVariant/',
    )

    await addCartLines([
      {
        merchandiseId: id,
        quantity: 1,
        sellingPlanId,
      },
    ])
    setCartModalOpen(true)
  }

  return (
    <StyledHero>
      <Content>
        {hero?.title && <Title>{hero.title}</Title>}
        <BottomContent>
          {hero?.description && <Description>{hero.description}</Description>}
          {hero?.productLink && (
            <InView
              onChange={(inView) => setShowStickyButton(!inView)}
              threshold={0}
            >
              <AddToCart
                appearance="badgeBlue"
                onClick={handleAddToCart}
                disabled={isVip}
              >
                {isVip ? (
                  <span>{t('alreadyMember')}</span>
                ) : (
                  <>
                    <span>{t('joinMembership')}</span>
                    <span>{t('membershipPrice')}</span>
                  </>
                )}
              </AddToCart>
            </InView>
          )}
        </BottomContent>
      </Content>
      <BackgroundWrapper>
        {hero?.media && (
          <Media
            {...hero.media}
            priority
            objectFit="cover"
            layout="fill"
            sizes={{ default: '100vw' }}
            withBackground
          />
        )}
      </BackgroundWrapper>

      {hero?.productLink && !isVip && (
        <StickyButtonContainer isVisible={showStickyButton && !isFooterVisible}>
          <StickyButton
            appearance="badgeBlue"
            onClick={handleAddToCart}
            disabled={isVip}
          >
            {isVip ? (
              <span>{t('alreadyMember')}</span>
            ) : (
              <>
                <span>{t('joinMembership')}</span>
                <span>{t('membershipPrice')}</span>
              </>
            )}
          </StickyButton>
        </StickyButtonContainer>
      )}
    </StyledHero>
  )
}
